import * as THREE from 'three';
import gsap from 'gsap';
import { textureLoader } from "../../utils/3d";
import earthVertexShader from '../../shaders/earth/globe/vertex.glsl'
import earthFragmentShader from '../../shaders/earth/globe/fragment.glsl'
import atmosphereVertexShader from '../../shaders/earth/atmosphere/vertex.glsl'
import atmosphereFragmentShader from '../../shaders/earth/atmosphere/fragment.glsl'

export default class {
    constructor(renderer) {
        this.renderer = renderer;
        this.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy()); 
        
        this.params = {
            atmosphereDayColor: '#0050ff',
            atmosphereTwilightColor: '#552312',
            
            cloudsCut: 0.07,
            cloudsOpacity: .6,
            cloudsNightOpacity: .04,
            
            reflectionColor: '#020f21',
            reflectionPow: 4.0,
            
            manLightsColor: '#FF7E28',
            nightDarkness: .92,

            CSB: new THREE.Vector3(1.0,1.0,.75)
        }
        
        this.init();
    }
    
    init() {
        this.object = new THREE.Group();        

        this.setGlobe();
        this.setAtmosphere();
    }

    setGlobe() {        
        let earthDayTexture, earthLightsTexture, earthSpecularCloudsTexture;
        
        let earthDayTexturePromise = new Promise(resolve => {
            earthDayTexture = textureLoader.load('/themes/populous/static/assets/images/earth_color.webp', resolve)
            earthDayTexture.colorSpace = THREE.SRGBColorSpace;
            earthDayTexture.anisotropy = this.anisotropy;            
        })

        let earthLightsTexturePromise = new Promise(resolve => {
            earthLightsTexture = textureLoader.load('/themes/populous/static/assets/images/earth_lights.webp', resolve)
            earthLightsTexture.anisotropy = this.anisotropy;            
        })

        let earthSpecularCloudsTexturePromise = new Promise(resolve => {
            // earthSpecularCloudsTexture = textureLoader.load('/themes/populous/static/assets/images/earth_spec_clouds.webp', resolve)
            earthSpecularCloudsTexture = textureLoader.load('/themes/populous/static/assets/images/earth_spec_clouds_lessclouds.webp', resolve)
            earthSpecularCloudsTexture.anisotropy = this.anisotropy;
        })

        this.globeGeometry = new THREE.SphereGeometry( 1, 128, 64 );
        this.globeMaterial = new THREE.ShaderMaterial({
            vertexShader: earthVertexShader,
            fragmentShader: earthFragmentShader,
            transparent: true,
            uniforms:
            {
                uReveal: new THREE.Uniform(0.0),

                uDayTexture: new THREE.Uniform(earthDayTexture),
                uLightsTexture: new THREE.Uniform(earthLightsTexture),                
                uSpecularCloudsTexture: new THREE.Uniform(earthSpecularCloudsTexture),

                uSunDirection: new THREE.Uniform(new THREE.Vector3(0, 0, 1)),

                uAtmosphereDayColor: new THREE.Uniform(new THREE.Color(this.params.atmosphereDayColor).convertLinearToSRGB()),
                uAtmosphereTwilightColor: new THREE.Uniform(new THREE.Color(this.params.atmosphereTwilightColor).convertLinearToSRGB()),

                uCloudsCut: new THREE.Uniform(this.params.cloudsCut),
                uCloudsOpacity: new THREE.Uniform(this.params.cloudsOpacity),
                uCloudsNightOpacity: new THREE.Uniform(this.params.cloudsNightOpacity),

                uManLightsColor: new THREE.Uniform(new THREE.Color(this.params.manLightsColor).convertLinearToSRGB()),
                uNightDarkness: new THREE.Uniform(this.params.nightDarkness),

                uReflectionColor: new THREE.Uniform(new THREE.Color(this.params.reflectionColor).convertLinearToSRGB()),
                uReflectionPow: new THREE.Uniform(this.params.reflectionPow),

                uCSB: new THREE.Uniform(this.params.CSB)
            }
        })
        this.globe = new THREE.Mesh( this.globeGeometry, this.globeMaterial );

        // Reveal effect
        Promise.all([earthDayTexturePromise, earthLightsTexturePromise, earthSpecularCloudsTexturePromise]).then(() => {
            const duration = 2;
            let tl = gsap.timeline({});
            tl.fromTo(this.globeMaterial.uniforms.uReveal, { value: 0 }, { value: 1, duration, ease: 'power4.in', repeat: 0 });
            tl.fromTo(this.atmosphereMaterial.uniforms.uReveal, { value: 0 }, { value: 1, duration, ease: 'power3.out', repeat: 0 }, duration*0.4);
        })
        
        this.object.add(this.globe);
    } 

    setAtmosphere() {
        // Atmosphere
        this.atmosphereMaterial = new THREE.ShaderMaterial({
            side: THREE.BackSide,
            transparent: true,
            vertexShader: atmosphereVertexShader,
            fragmentShader: atmosphereFragmentShader,
            uniforms:
            {
                uReveal: new THREE.Uniform(0.0),
                
                uSunDirection: new THREE.Uniform(new THREE.Vector3(0, 0, 1)),
                uAtmosphereDayColor: new THREE.Uniform(new THREE.Color(this.params.atmosphereDayColor).convertLinearToSRGB()),
                uAtmosphereTwilightColor: new THREE.Uniform(new THREE.Color(this.params.atmosphereTwilightColor).convertLinearToSRGB()),
                uCSB: new THREE.Uniform(this.params.CSB)
            },
        })

        this.atmosphere = new THREE.Mesh(this.globeGeometry, this.atmosphereMaterial)
        this.atmosphere.scale.set(1.066, 1.066, 1.066)
        this.object.add(this.atmosphere)
    }

    update(sunDirection) {
        this.globeMaterial.uniforms.uSunDirection.value.copy(sunDirection)
        this.atmosphereMaterial.uniforms.uSunDirection.value.copy(sunDirection)
    }
}